import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  Alert,
  Col,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import Card from "../card/Card";
import { Button, Toast } from "../ui";
import SynchronizationService from "../../services/synchronizationService";

dayjs.locale("pt-br");

const { Text, Title } = Typography;

// Rótulos amigáveis para o select "Tipo de regra" no modal. Os valores internos
// (chave do mapa) são o contrato com o backend/Integrador e não foram alterados.
const RULE_TYPE_LABELS = {
  DaysAfter: "Alguns dias depois",
  FixedDayOfMonth: "Em um dia fixo do mês",
  DayOfWeek: "No próximo dia da semana",
  DaysOfMonth: "Em determinados dias do mês",
};

// Apresentação amigável das SourcePolicies. `value` é o enum/payload real, inalterado.
// `help` reflete a semântica confirmada pela auditoria do Integrador: a exigência de
// título "em aberto" (Saldo > 0) vale SOMENTE para FormalOnlyWhenOpen — nas demais
// policies, o fallback/uso do Contas a Receber considera qualquer título vinculado.
const SOURCE_POLICIES = [
  {
    value: "PreferChannelRule",
    label: "Usar a regra desta loja",
    help: "Calcula a previsão usando a regra definida abaixo para esta loja. Se a regra não puder ser aplicada, usamos o vencimento de uma conta a receber vinculada no Bling, quando houver.",
    usesChannelRule: true,
    channelRuleRole: "primary",
  },
  {
    value: "PreferFormalReceivable",
    label: "Usar primeiro o Contas a Receber",
    help: "Se existir uma conta a receber vinculada no Bling, usamos o vencimento dela. Caso contrário, usamos a regra desta loja.",
    usesChannelRule: true,
    channelRuleRole: "fallback",
  },
  {
    value: "FormalOnlyWhenOpen",
    label: "Somente Contas a Receber em aberto",
    help: "Usa o vencimento apenas das contas a receber que ainda estão em aberto no Bling.",
    usesChannelRule: false,
  },
];

const WEEKEND_POLICY_OPTIONS = [
  { value: "Keep", label: "Manter a data" },
  { value: "NextBusinessDay", label: "Mover para o próximo dia útil" },
  { value: "PreviousBusinessDay", label: "Mover para o dia útil anterior" },
];

// Forma curta, para a nota secundária da coluna "Regra" da listagem — nunca o enum cru.
const WEEKEND_POLICY_SHORT_LABELS = {
  Keep: "manter a data",
  NextBusinessDay: "próximo dia útil",
  PreviousBusinessDay: "dia útil anterior",
};

const WEEK_DAYS = [
  ["Sunday", "Domingo"],
  ["Monday", "Segunda-feira"],
  ["Tuesday", "Terça-feira"],
  ["Wednesday", "Quarta-feira"],
  ["Thursday", "Quinta-feira"],
  ["Friday", "Sexta-feira"],
  ["Saturday", "Sábado"],
];

const DEFAULT_FORM = {
  sourcePolicy: "PreferChannelRule",
  ruleType: "DaysAfter",
  daysAfter: 0,
  fixedDayOfMonth: null,
  dayOfWeek: null,
  daysOfMonthText: "",
  weekendPolicy: "Keep",
};

export const parseDaysOfMonth = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number);

export const getForecastValidationError = (form) => {
  if (form.ruleType === "DaysAfter") {
    if (!Number.isInteger(form.daysAfter) || form.daysAfter < 0 || form.daysAfter > 3650) {
      return "Informe de 0 a 3650 dias após a emissão.";
    }
  }

  if (form.ruleType === "FixedDayOfMonth") {
    if (!Number.isInteger(form.fixedDayOfMonth) || form.fixedDayOfMonth < 1 || form.fixedDayOfMonth > 31) {
      return "Informe um dia fixo entre 1 e 31.";
    }
  }

  if (form.ruleType === "DayOfWeek" && !WEEK_DAYS.some(([value]) => value === form.dayOfWeek)) {
    return "Selecione o dia da semana.";
  }

  if (form.ruleType === "DaysOfMonth") {
    const days = parseDaysOfMonth(form.daysOfMonthText);
    if (!days.length || days.some((day) => !Number.isInteger(day) || day < 1 || day > 31)) {
      return "Informe ao menos um dia entre 1 e 31.";
    }
    if (new Set(days).size !== days.length) {
      return "Não repita dias do mês.";
    }
  }

  return null;
};

export const buildForecastPayload = (form) => ({
  sourcePolicy: form.sourcePolicy,
  basisType: "NFeEmissionDate",
  ruleType: form.ruleType,
  daysAfter: form.ruleType === "DaysAfter" ? form.daysAfter : null,
  fixedDayOfMonth:
    form.ruleType === "FixedDayOfMonth" ? form.fixedDayOfMonth : null,
  dayOfWeek: form.ruleType === "DayOfWeek" ? form.dayOfWeek : null,
  daysOfMonth:
    form.ruleType === "DaysOfMonth" ? parseDaysOfMonth(form.daysOfMonthText) : [],
  weekendPolicy: form.weekendPolicy,
});

export const summarizeForecastRule = (rule) => {
  if (!rule) return "Não configurada";
  switch (rule.ruleType) {
    case "DaysAfter":
      return `${rule.daysAfter} dia(s) após a emissão`;
    case "FixedDayOfMonth":
      return `Dia ${rule.fixedDayOfMonth} de cada mês`;
    case "DayOfWeek":
      return `Próxima ${WEEK_DAYS.find(([value]) => value === rule.dayOfWeek)?.[1] || rule.dayOfWeek}`;
    case "DaysOfMonth":
      return `Dias ${(rule.daysOfMonth || []).join(", ")}`;
    default:
      return rule.ruleType || "Não configurada";
  }
};

// Data ilustrativa fixa e estável (quarta-feira), só para o exemplo em linguagem natural — não chama o Backend.
const EXAMPLE_EMISSION_DATE = dayjs("2026-01-14");

const applyWeekendPolicyToExample = (date, weekendPolicy) => {
  const weekday = date.day(); // 0 = domingo, 6 = sábado
  if (weekendPolicy === "NextBusinessDay") {
    if (weekday === 6) return date.add(2, "day");
    if (weekday === 0) return date.add(1, "day");
  }
  if (weekendPolicy === "PreviousBusinessDay") {
    if (weekday === 6) return date.subtract(1, "day");
    if (weekday === 0) return date.subtract(2, "day");
  }
  return date;
};

const formatExampleDate = (date) => date.format("dddd, DD [de] MMMM");

// Exemplo em linguagem natural para o modal. Calcula data real apenas para os tipos
// de regra com aritmética inequívoca (DaysAfter, DayOfWeek); para os demais, descreve
// a regra em texto, sem presumir detalhes de cálculo (ex.: rolagem de mês) que não
// estão implementados neste repositório (a lógica real roda no Integrador).
export const buildForecastExample = (form) => {
  const emissionLabel = formatExampleDate(EXAMPLE_EMISSION_DATE);

  if (form.ruleType === "DaysAfter") {
    const days = Number.isInteger(form.daysAfter) ? form.daysAfter : 0;
    const raw = EXAMPLE_EMISSION_DATE.add(days, "day");
    const result = applyWeekendPolicyToExample(raw, form.weekendPolicy);
    const shifted = !result.isSame(raw, "day");
    return `Exemplo: NF-e emitida em ${emissionLabel} → previsão para ${formatExampleDate(result)}${shifted ? " (ajustada por cair em fim de semana)" : ""}.`;
  }

  if (form.ruleType === "DayOfWeek") {
    const targetIndex = WEEK_DAYS.findIndex(([value]) => value === form.dayOfWeek);
    if (targetIndex === -1) return null;
    let diff = (targetIndex - EXAMPLE_EMISSION_DATE.day() + 7) % 7;
    if (diff === 0) diff = 7;
    const raw = EXAMPLE_EMISSION_DATE.add(diff, "day");
    const result = applyWeekendPolicyToExample(raw, form.weekendPolicy);
    const shifted = !result.isSame(raw, "day");
    return `Exemplo: NF-e emitida em ${emissionLabel} → previsão para ${formatExampleDate(result)}${shifted ? " (ajustada por cair em fim de semana)" : ""}.`;
  }

  if (form.ruleType === "FixedDayOfMonth" && form.fixedDayOfMonth) {
    return `Exemplo: a previsão usa o dia ${form.fixedDayOfMonth} do mês a partir da emissão da NF-e (${emissionLabel}). Se cair em fim de semana, vale o tratamento definido abaixo.`;
  }

  if (form.ruleType === "DaysOfMonth") {
    const days = parseDaysOfMonth(form.daysOfMonthText);
    if (!days.length) return null;
    return `Exemplo: a previsão usa o próximo dia informado (${days.join(", ")}) a partir da emissão da NF-e (${emissionLabel}). Se cair em fim de semana, vale o tratamento definido abaixo.`;
  }

  return null;
};

// Coluna "Regra" quando não há ForecastRule própria. Semântica confirmada pela auditoria
// do Integrador: sem regra ativa, se existir um Contas a Receber formal vinculado à NFe,
// o Forecast é gerado automaticamente pelo vencimento dele; se não existir, não há Forecast.
// A listagem (GetForecastRules/ForecastChannelRuleResponse em SyncController.cs) não traz
// um campo indicando se existe Contas a Receber vinculado a cada loja — por isso o texto
// usa "quando houver" em vez de afirmar que o Forecast sempre é gerado nesse caso.
export const DEFAULT_FALLBACK_RULE_PRIMARY_TEXT = "Vencimento do Contas a Receber";
export const DEFAULT_FALLBACK_RULE_SECONDARY_TEXT =
  "Usa automaticamente o vencimento do Contas a Receber vinculado no Bling, quando houver um.";

// Linha "estado da loja no Bling" da coluna "Loja" — nunca confundir com o status da regra.
export const describeChannelStatusLine = (channel) =>
  `${channel?.tipo || "Tipo não informado"} · ${channel?.situacao === 2 ? "Loja inativa no Bling" : "Loja ativa no Bling"}`;

// Nota secundária de fim de semana para a coluna "Regra" da listagem. Nunca retorna o
// enum cru; retorna null quando a policy da regra não usa a regra do canal (ex.:
// FormalOnlyWhenOpen), já que o fim de semana não participa do cálculo nesse caso.
export const describeForecastWeekendNote = (rule) => {
  if (!rule?.weekendPolicy) return null;
  const policyMeta = SOURCE_POLICIES.find((policy) => policy.value === rule.sourcePolicy);
  if (policyMeta?.usesChannelRule === false) return null;
  const label = WEEKEND_POLICY_SHORT_LABELS[rule.weekendPolicy];
  return label ? `Se cair no fim de semana: ${label}` : null;
};

// Conteúdo da coluna "Regra": nunca expõe versão (v1, v2...) nem enum cru.
export const describeForecastRuleCell = (channel) => {
  if (!channel?.rule) {
    return { primary: DEFAULT_FALLBACK_RULE_PRIMARY_TEXT, secondary: DEFAULT_FALLBACK_RULE_SECONDARY_TEXT };
  }
  return {
    primary: summarizeForecastRule(channel.rule),
    secondary: describeForecastWeekendNote(channel.rule),
  };
};

// Três status reais possíveis para uma loja, na mesma ordem usada no filtro da listagem.
// Fonte única: tanto a coluna "Status" quanto o filtro derivam destes valores.
export const FORECAST_STATUS_OPTIONS = [
  { value: "default", label: "Padrão", color: "blue" },
  { value: "active", label: "Regra ativa", color: "green" },
  { value: "disabled", label: "Regra desativada", color: "orange" },
];

// Distingue loja sem ForecastRule (usa o fallback padrão pelo Contas a Receber, quando
// houver), regra ativa e regra desativada — três conceitos diferentes que não podem ser
// misturados.
export const getForecastStatusKey = (channel) => {
  if (!channel?.configured) return "default";
  if (channel?.rule?.active) return "active";
  return "disabled";
};

// Conteúdo da coluna "Status".
export const describeForecastStatus = (channel) => {
  const { label, color } = FORECAST_STATUS_OPTIONS.find(
    (option) => option.value === getForecastStatusKey(channel),
  );
  return { label, color };
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 400) return "A regra informada não é válida para este canal.";
  if (status === 401 || status === 403) return "Você não tem permissão para alterar as regras de previsão.";
  if (status === 404) return "A integração ou o canal não foi encontrado.";
  if (status === 502 || status === 504) return "Não foi possível consultar o serviço de previsão agora. Tente novamente.";
  return "Não foi possível concluir a operação de previsão. Tente novamente.";
};

const ForecastRulesConfig = ({ integrationId, canEdit }) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadRules = async () => {
    if (!integrationId) return;
    setLoading(true);
    setError(null);
    try {
      setChannels(await SynchronizationService.getForecastRules(integrationId));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, [integrationId]);

  const filteredChannels = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return channels.filter((channel) => {
      const matchesSearch =
        !normalizedSearch ||
        (channel.descricao || `Loja ${channel.externalCanalVendaId}`)
          .toLowerCase()
          .includes(normalizedSearch);
      const matchesStatus =
        statusFilter === "all" || getForecastStatusKey(channel) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [channels, search, statusFilter]);

  const selectedSourcePolicy = useMemo(
    () => SOURCE_POLICIES.find((policy) => policy.value === form.sourcePolicy),
    [form.sourcePolicy],
  );

  const showChannelRule = selectedSourcePolicy?.usesChannelRule !== false;
  const channelRuleSectionTitle =
    selectedSourcePolicy?.channelRuleRole === "fallback" ? "Regra alternativa da loja" : "Regra desta loja";
  const channelRuleSectionHelp =
    selectedSourcePolicy?.channelRuleRole === "fallback"
      ? "Usada quando não houver uma conta a receber aplicável no Bling."
      : null;

  const forecastExample = useMemo(() => {
    if (!showChannelRule) {
      return "Exemplo: se a venda tiver uma conta a receber em aberto no Bling, a previsão usa o vencimento dela. Sem conta a receber em aberto, esta loja fica sem previsão.";
    }
    return buildForecastExample(form);
  }, [showChannelRule, form]);

  const openEditor = (channel) => {
    const rule = channel.rule;
    setEditingChannel(channel);
    setForm({
      sourcePolicy: rule?.sourcePolicy || DEFAULT_FORM.sourcePolicy,
      ruleType: rule?.ruleType || DEFAULT_FORM.ruleType,
      daysAfter: rule?.daysAfter ?? DEFAULT_FORM.daysAfter,
      fixedDayOfMonth: rule?.fixedDayOfMonth ?? null,
      dayOfWeek: rule?.dayOfWeek ?? null,
      daysOfMonthText: (rule?.daysOfMonth || []).join(", "),
      weekendPolicy: rule?.weekendPolicy || DEFAULT_FORM.weekendPolicy,
    });
  };

  const closeEditor = () => {
    if (!saving) setEditingChannel(null);
  };

  const saveRule = async () => {
    const validationError = getForecastValidationError(form);
    if (validationError) {
      Toast.error(validationError);
      return;
    }

    setSaving(true);
    try {
      await SynchronizationService.upsertForecastRule(
        integrationId,
        editingChannel.externalCanalVendaId,
        buildForecastPayload(form),
      );
      Toast.success("Regra de previsão salva.");
      setEditingChannel(null);
      await loadRules();
    } catch (requestError) {
      Toast.error(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const deactivateRule = async (channel) => {
    try {
      await SynchronizationService.deactivateForecastRule(
        integrationId,
        channel.externalCanalVendaId,
      );
      Toast.success("Regra de previsão desativada.");
      await loadRules();
    } catch (requestError) {
      Toast.error(getErrorMessage(requestError));
    }
  };

  const columns = [
    {
      title: "Loja",
      key: "channel",
      render: (_, channel) => (
        <div>
          <Text strong>{channel.descricao || `Loja ${channel.externalCanalVendaId}`}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {describeChannelStatusLine(channel)}
          </Text>
        </div>
      ),
    },
    {
      title: "Regra",
      key: "rule",
      render: (_, channel) => {
        const { primary, secondary } = describeForecastRuleCell(channel);
        return (
          <div>
            <Text>{primary}</Text>
            {secondary && (
              <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                {secondary}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, channel) => {
        const { label, color } = describeForecastStatus(channel);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Ações",
      key: "actions",
      render: (_, channel) =>
        canEdit ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button size="small" onClick={() => openEditor(channel)}>
              {channel.configured && channel.rule?.active ? "Editar" : "Configurar"}
            </Button>
            {channel.configured && channel.rule?.active && (
              <Popconfirm
                title="Desativar esta regra de previsão?"
                description="A loja ficará sem regra própria até uma nova configuração."
                okText="Desativar"
                cancelText="Cancelar"
                onConfirm={() => deactivateRule(channel)}
              >
                <Button size="small" danger>
                  Desativar
                </Button>
              </Popconfirm>
            )}
          </div>
        ) : (
          <Text type="secondary">Somente leitura</Text>
        ),
    },
  ];

  return (
    <Card style={{ marginTop: 24, borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 12 }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>Previsão de recebimentos</Title>
          <Text type="secondary">Defina uma regra por loja do Bling.</Text>
        </div>
        <Button size="small" onClick={loadRules} loading={loading}>Atualizar</Button>
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="A base da regra é sempre a data de emissão da NF-e."
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {!loading && channels.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={14} md={10}>
            <Input.Search
              placeholder="Pesquisar loja..."
              allowClear
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Col>
          <Col xs={24} sm={10} md={8}>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "Todos" },
                ...FORECAST_STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
              ]}
            />
          </Col>
        </Row>
      )}
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}><Spin /></div>
      ) : channels.length ? (
        <Table
          rowKey="externalCanalVendaId"
          columns={columns}
          dataSource={filteredChannels}
          pagination={false}
          scroll={{ x: 760, y: 420 }}
          locale={{
            emptyText: (
              <Empty description="Nenhuma loja encontrada para os filtros informados." />
            ),
          }}
        />
      ) : (
        <Empty description="Nenhuma loja retornada pela integração." />
      )}

      <Modal
        open={Boolean(editingChannel)}
        title={
          editingChannel ? (
            <div>
              <div>{`Previsão de recebimento — ${editingChannel.descricao || editingChannel.externalCanalVendaId}`}</div>
              <Text type="secondary" style={{ fontWeight: 400, fontSize: 13, display: "block", marginTop: 2 }}>
                Defina quando as vendas desta loja devem aparecer no Fluxo de Caixa previsto.
              </Text>
            </div>
          ) : (
            "Previsão de recebimento"
          )
        }
        onCancel={closeEditor}
        onOk={saveRule}
        okText="Salvar previsão"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <div style={{ display: "grid", gap: 18 }}>
          <div>
            <Text strong>Como definir a data prevista?</Text>
            <Select
              value={form.sourcePolicy}
              style={{ width: "100%", marginTop: 4 }}
              options={SOURCE_POLICIES.map(({ value, label }) => ({ value, label }))}
              onChange={(sourcePolicy) => setForm((previous) => ({ ...previous, sourcePolicy }))}
            />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
              {selectedSourcePolicy?.help}
            </Text>
          </div>

          {showChannelRule && (
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 12 }}>
              <Text strong>{channelRuleSectionTitle}</Text>
              {channelRuleSectionHelp && (
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                  {channelRuleSectionHelp}
                </Text>
              )}
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6, marginBottom: 10 }}>
                A partir de: Emissão da NF-e
              </Text>

              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <Text strong>Quando você recebe?</Text>
                  <Select
                    value={form.ruleType}
                    style={{ width: "100%", marginTop: 4 }}
                    options={Object.entries(RULE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                    onChange={(ruleType) => setForm((previous) => ({ ...previous, ruleType }))}
                  />
                </div>
                {form.ruleType === "DaysAfter" && (
                  <div><Text strong>Dias após emissão</Text><InputNumber min={0} max={3650} value={form.daysAfter} style={{ width: "100%", marginTop: 4 }} onChange={(daysAfter) => setForm((previous) => ({ ...previous, daysAfter }))} /></div>
                )}
                {form.ruleType === "FixedDayOfMonth" && (
                  <div><Text strong>Dia fixo do mês</Text><InputNumber min={1} max={31} value={form.fixedDayOfMonth} style={{ width: "100%", marginTop: 4 }} onChange={(fixedDayOfMonth) => setForm((previous) => ({ ...previous, fixedDayOfMonth }))} /></div>
                )}
                {form.ruleType === "DayOfWeek" && (
                  <div><Text strong>Dia da semana</Text><Select value={form.dayOfWeek} style={{ width: "100%", marginTop: 4 }} placeholder="Selecione" options={WEEK_DAYS.map(([value, label]) => ({ value, label }))} onChange={(dayOfWeek) => setForm((previous) => ({ ...previous, dayOfWeek }))} /></div>
                )}
                {form.ruleType === "DaysOfMonth" && (
                  <div><Text strong>Dias do mês</Text><Input value={form.daysOfMonthText} placeholder="Ex.: 5, 15, 25" style={{ marginTop: 4 }} onChange={(event) => setForm((previous) => ({ ...previous, daysOfMonthText: event.target.value }))} /><Text type="secondary" style={{ fontSize: 12 }}>Separe os dias por vírgula.</Text></div>
                )}
                <div>
                  <Text strong>Se a data cair no fim de semana</Text>
                  <Select value={form.weekendPolicy} style={{ width: "100%", marginTop: 4 }} options={WEEKEND_POLICY_OPTIONS} onChange={(weekendPolicy) => setForm((previous) => ({ ...previous, weekendPolicy }))} />
                </div>
              </div>
            </div>
          )}

          {forecastExample && (
            <Alert type="info" showIcon message={forecastExample} style={{ fontSize: 12 }} />
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default ForecastRulesConfig;
